import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import SiteShell from "../components/layout/SiteShell";
import SEO from "../components/seo/SEO";
import { appendStorySubmission } from "../lib/storySubmissions";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const featuredStories = [
  {
    title: "A morning ritual",
    blend: "Gold Sea Moss + Ginger + Turmeric",
    quote:
      "I keep mine in the fridge and add a spoonful to my morning routine. The ginger and turmeric blend feels warming, simple, and easy to stay consistent with.",
  },
  {
    title: "Post-gym routine",
    blend: "Purple Sea Moss + Moringa + Bladderwrack",
    quote:
      "I wanted something I could add to my protein shake without overcomplicating things. This blend fits into my routine and feels clean.",
  },
  {
    title: "Simple daily wellness",
    blend: "Gold Sea Moss Plain",
    quote:
      "I started with plain gold sea moss because I wanted to keep it simple. The texture is smooth and it is easy to add into drinks or oats.",
  },
];

export default function StoriesPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [blend, setBlend] = useState("");
  const [story, setStory] = useState("");
  const [permissionMarketing, setPermissionMarketing] = useState(false);
  const [acknowledgeEditing, setAcknowledgeEditing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || !blend.trim() || !story.trim()) {
      setError("Please fill in name, email, blend, and your story.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!permissionMarketing || !acknowledgeEditing) {
      setError("Please confirm both checkboxes to submit.");
      return;
    }
    appendStorySubmission({
      name: name.trim(),
      email: email.trim(),
      orderNumber: orderNumber.trim(),
      blend: blend.trim(),
      story: story.trim(),
      permissionMarketing,
      acknowledgeEditing,
    });
    setSubmitted(true);
    window.scrollTo({ top: document.getElementById("share-story")?.offsetTop ?? 0, behavior: "smooth" });
  };

  return (
    <SiteShell theme="dark">
      <SEO
        title="Tell Your MossMatrix Story | MossMatrix"
        description="Used MossMatrix as part of your daily routine? Share your story, browse routine placeholders, and explore our future video library. UK-only fresh sea moss gel."
        path="/stories"
        ogType="website"
      />

      <main className="mm-dark-content bg-void">
        {/* Hero */}
        <section className="relative border-b border-cream overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#dfe8e2]/40 via-offwhite to-cream/30 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div {...fadeUp}>
                <span className="text-xs tracking-[0.25em] uppercase text-botanical/70">Community</span>
                <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-forest mt-4 leading-[1.05]">
                  Tell Your MossMatrix Story
                </h1>
                <p className="mt-6 text-base md:text-lg text-botanical/85 leading-relaxed max-w-lg">
                  Used MossMatrix as part of your daily routine? We would love to hear how you use it. Personal experiences only, not medical advice.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="#share-story"
                    className="inline-flex rounded-full bg-forest text-cream px-7 py-3.5 text-sm font-medium hover:bg-botanical transition shadow-soft"
                  >
                    Share your story
                  </a>
                  <Link
                    to="/shop"
                    className="inline-flex rounded-full border border-forest/25 bg-white text-forest px-7 py-3.5 text-sm font-medium hover:bg-cream transition"
                  >
                    Shop the range
                  </Link>
                </div>
              </motion.div>
              <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}>
                <div className="rounded-[2rem] overflow-hidden border border-cream/90 bg-cream/30 min-h-[320px] lg:min-h-[380px] shadow-hover">
                  <img
                    src={`${process.env.PUBLIC_URL || ""}/images/stories-hero-jar-stone.png`}
                    alt="MossMatrix Premium Sea Moss Gel jar on a stone slab with soft linen and dried botanicals"
                    className="w-full h-full min-h-[320px] lg:min-h-[380px] object-cover editorial-img"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Featured stories */}
        <section className="py-20 md:py-28 border-b border-cream bg-white/80">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <motion.div {...fadeUp} className="max-w-2xl mb-14">
              <span className="text-xs tracking-[0.25em] uppercase text-botanical/70">Featured</span>
              <h2 className="font-serif text-3xl md:text-4xl text-forest mt-3">Rituals from the community</h2>
              <p className="mt-4 text-sm md:text-base text-botanical/80 leading-relaxed">
                Demo cards for layout and tone. Future entries will be moderated, edited for clarity, and published only with permission.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {featuredStories.map((s, i) => (
                <motion.article
                  key={s.title}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                  className="rounded-[1.75rem] border border-cream bg-[#FAF7F0] p-6 md:p-8 shadow-soft flex flex-col"
                >
                  <h3 className="font-serif text-2xl text-forest">{s.title}</h3>
                  <p className="mt-2 text-[11px] tracking-[0.2em] uppercase text-gold">{s.blend}</p>
                  <blockquote className="mt-5 text-sm text-botanical/85 leading-relaxed flex-1 border-t border-cream/90 pt-5">
                    &ldquo;{s.quote}&rdquo;
                  </blockquote>
                  <p className="mt-4 text-[10px] uppercase tracking-widest text-botanical/50">Demo placeholder</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Video coming soon — no embeds until we have permissioned content */}
        <section className="py-16 md:py-20 bg-[#dfe8e2]/35 border-b border-cream" aria-labelledby="stories-video-heading">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <motion.div
              {...fadeUp}
              className="max-w-2xl rounded-2xl border border-cream bg-white/90 px-6 py-7 md:px-8 md:py-8 shadow-soft"
            >
              <span className="text-xs tracking-[0.25em] uppercase text-botanical/70">Coming later</span>
              <h2 id="stories-video-heading" className="font-serif text-2xl md:text-3xl text-forest mt-3 leading-tight">
                Video routines
              </h2>
              <p className="mt-4 text-sm md:text-base text-botanical/85 leading-relaxed m-0">
                Video routines coming later. For now, we are collecting customer stories, simple routines, and honest feedback.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Form */}
        <section id="share-story" className="py-20 md:py-28 bg-offwhite scroll-mt-28">
          <div className="max-w-2xl mx-auto px-6 md:px-10">
            <motion.div {...fadeUp}>
              <span className="text-xs tracking-[0.25em] uppercase text-botanical/70">Contribute</span>
              <h2 className="font-serif text-3xl md:text-4xl text-forest mt-3">Share your story</h2>
              <p className="mt-4 text-sm text-botanical/80 leading-relaxed">
                Tell us how you use your blend. We read every submission. Nothing is published without review and consent.
              </p>
            </motion.div>

            <p className="mt-8 text-xs text-botanical/75 leading-relaxed rounded-2xl border border-cream bg-white/90 px-5 py-4 shadow-soft">
              Customer stories are personal experiences, not medical advice. MossMatrix is a food product and should be used as part of a
              balanced lifestyle.
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-10 rounded-2xl border border-cream bg-white p-8 shadow-hover flex gap-4"
                role="status"
              >
                <CheckCircle2 className="w-8 h-8 text-gold shrink-0 mt-0.5" strokeWidth={1.4} aria-hidden />
                <div>
                  <p className="font-serif text-xl text-forest">Thank you. Your story has been received.</p>
                  <p className="mt-2 text-sm text-botanical/80 leading-relaxed">We will review it before publishing.</p>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-10 space-y-5">
                <div>
                  <label htmlFor="story-name" className="block text-xs font-medium uppercase tracking-widest text-botanical/70 mb-2">
                    Name
                  </label>
                  <input
                    id="story-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-2xl border border-cream bg-white px-4 py-3 text-sm text-forest placeholder:text-botanical/40 shadow-soft"
                    placeholder="First name or how you would like to be credited"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label htmlFor="story-email" className="block text-xs font-medium uppercase tracking-widest text-botanical/70 mb-2">
                    Email
                  </label>
                  <input
                    id="story-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-2xl border border-cream bg-white px-4 py-3 text-sm text-forest placeholder:text-botanical/40 shadow-soft"
                    placeholder="So we can reply if needed"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label htmlFor="story-order" className="block text-xs font-medium uppercase tracking-widest text-botanical/70 mb-2">
                    Order number <span className="text-botanical/50 normal-case tracking-normal">(optional)</span>
                  </label>
                  <input
                    id="story-order"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    className="w-full rounded-2xl border border-cream bg-white px-4 py-3 text-sm text-forest placeholder:text-botanical/40 shadow-soft"
                    placeholder="If you have one"
                  />
                </div>
                <div>
                  <label htmlFor="story-blend" className="block text-xs font-medium uppercase tracking-widest text-botanical/70 mb-2">
                    Blend used
                  </label>
                  <input
                    id="story-blend"
                    value={blend}
                    onChange={(e) => setBlend(e.target.value)}
                    className="w-full rounded-2xl border border-cream bg-white px-4 py-3 text-sm text-forest placeholder:text-botanical/40 shadow-soft"
                    placeholder="e.g. Gold 500ml + ginger + turmeric"
                  />
                </div>
                <div>
                  <label htmlFor="story-body" className="block text-xs font-medium uppercase tracking-widest text-botanical/70 mb-2">
                    Your story
                  </label>
                  <textarea
                    id="story-body"
                    value={story}
                    onChange={(e) => setStory(e.target.value)}
                    rows={6}
                    className="w-full rounded-2xl border border-cream bg-white px-4 py-3 text-sm text-forest placeholder:text-botanical/40 shadow-soft resize-y min-h-[140px]"
                    placeholder="How does MossMatrix fit your day? Keep it personal and routine-focused."
                  />
                </div>

                <label className="flex gap-3 items-start cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={permissionMarketing}
                    onChange={(e) => setPermissionMarketing(e.target.checked)}
                    className="mt-1 rounded border-cream text-forest focus:ring-forest"
                  />
                  <span className="text-sm text-botanical/85 leading-relaxed">
                    I give MossMatrix permission to use my story, first name, blend choice, and submitted media in marketing.
                  </span>
                </label>

                <label className="flex gap-3 items-start cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={acknowledgeEditing}
                    onChange={(e) => setAcknowledgeEditing(e.target.checked)}
                    className="mt-1 rounded border-cream text-forest focus:ring-forest"
                  />
                  <span className="text-sm text-botanical/85 leading-relaxed">
                    I understand MossMatrix may edit my story for length and remove medical claims.
                  </span>
                </label>

                {error ? <p className="text-sm text-red-800 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p> : null}

                <button
                  type="submit"
                  className="w-full sm:w-auto inline-flex justify-center rounded-full bg-forest text-cream px-8 py-3.5 text-sm font-medium hover:bg-botanical transition shadow-soft"
                >
                  Submit story
                </button>
              </form>
            )}
          </div>
        </section>

        {/* Closing CTA strip */}
        <section className="py-14 border-t border-cream bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
            <p className="font-serif text-2xl text-forest">Ready for your next jar?</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to="/#choose-sea-moss"
                className="inline-flex rounded-full bg-forest text-cream px-6 py-3 text-sm font-medium hover:bg-botanical transition"
              >
                Choose your sea moss
              </Link>
              <Link
                to="/shop"
                className="inline-flex rounded-full border border-forest/25 bg-white text-forest px-6 py-3 text-sm font-medium hover:bg-cream transition"
              >
                Shop now
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SiteShell>
  );
}
